import React, { useState, useEffect } from 'react';
import { ChevronDown, ShoppingBag, Home, Box, Users, List, Star, FileText, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../images/Logo1.png';

const Sidebar = ({ onSelect }) => {
  const [openSections, setOpenSections] = useState({});
  const [openSubItems, setOpenSubItems] = useState({});
  const [selectedItem, setSelectedItem] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuSections = [
    {
      title: 'General',
      items: [
        { name: 'Dashboard', icon: <Home className="h-5 w-5 mr-3" />, subcategories: [] },
        {
          name: 'Products',
          icon: <Box className="h-5 w-5 mr-3" />,
          subcategories: [
            { name: 'ProductList' },
            { name: 'CreateProduct' },
          ],
        },
        {
          name: 'Category',
          icon: <FileText className="h-5 w-5 mr-3" />,
          subcategories: [
            { name: 'Add Category' },
            { name: 'Category Management' },
          ],
        },
        {
          name: 'Orders',
          icon: <ShoppingBag className="h-5 w-5 mr-3" />,
          subcategories: [
            { name: 'All Orders' },
            { name: 'Order Management' },
          ],
        },
      ],
    },
    {
      title: 'Website Changes',
      items: [
        { name: "HomeBannerChanges", icon: <Home className="h-5 w-5 mr-3" /> },
        { name: "SponoserPage", icon: <Users className="h-5 w-5 mr-3" /> },
      ],
    },
  ];

  const toggleSection = (sectionTitle) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }));
  };

  const toggleSubItem = (itemName) => {
    setOpenSubItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const handleSelect = (itemName) => {
    setSelectedItem(itemName);
    onSelect(itemName);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const sidebarClasses = `
    ${isMobile ? 'fixed inset-y-0 left-0 z-50 w-72' : 'w-72'}
    ${isMobile && !isOpen ? '-translate-x-full' : 'translate-x-0'}
    transition-all duration-300 ease-in-out
    bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900
    text-gray-100 shadow-xl h-screen overflow-hidden
  `;

  return (
    <>
      {isMobile && (
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="fixed top-4 left-4 z-50 p-2 bg-purple-700 text-white rounded-md shadow-lg"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </motion.button>
      )}
      <aside className={sidebarClasses}>
        <motion.div 
          className="p-6 flex items-center space-x-4 border-b border-indigo-700 bg-gradient-to-r from-purple-800 via-indigo-700 to-blue-800 rounded-b-2xl shadow-lg"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          
          <span className="text-2xl font-bold text-white transition-all duration-300 transform hover:text-purple-300 truncate">
            Ecommerce Panel
          </span>
        </motion.div>

        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-indigo-600 scrollbar-track-indigo-300">
          <nav className="py-6">
            {menuSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mb-4">
                <motion.div
                  className={`flex justify-between items-center cursor-pointer px-6 py-3 text-lg font-semibold rounded-lg transition-all duration-300 ease-in-out ${
                    openSections[section.title]
                      ? 'bg-indigo-700 text-white'
                      : 'text-indigo-200 hover:bg-indigo-800 hover:text-white'
                  }`}
                  onClick={() => toggleSection(section.title)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>{section.title}</span>
                  <motion.div
                    animate={{ rotate: openSections[section.title] ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="h-5 w-5" />
                  </motion.div>
                </motion.div>
                <AnimatePresence>
                  {openSections[section.title] && (
                    <motion.ul
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-1 mt-2"
                    >
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex}>
                          <motion.button
                            onClick={() => {
                              if (!item.subcategories?.length) {
                                handleSelect(item.name);
                              }
                              toggleSubItem(item.name);
                            }}
                            className={`flex items-center px-6 py-2 rounded-lg transition-all duration-200 ease-in-out w-full text-left ${
                              selectedItem === item.name || openSubItems[item.name]
                                ? 'bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white shadow-md'
                                : 'text-indigo-200 hover:bg-indigo-800 hover:text-white'
                            }`}
                            whileHover={{ scale: 1.02, x: 5 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {item.icon}
                            <span className="truncate">{item.name}</span>
                            {item.subcategories?.length > 0 && (
                              <motion.div
                                className="ml-auto"
                                animate={{ rotate: openSubItems[item.name] ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </motion.div>
                            )}
                          </motion.button>
                          <AnimatePresence>
                            {openSubItems[item.name] && item.subcategories?.length > 0 && (
                              <motion.ul
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="ml-8 space-y-1 mt-1"
                              >
                                {item.subcategories.map((subcategory, subIndex) => (
                                  <motion.li key={subIndex}>
                                    <motion.button
                                      onClick={() => handleSelect(subcategory.name)}
                                      className={`flex items-center px-4 py-2 rounded-lg transition-all duration-200 ease-in-out w-full text-left ${
                                        selectedItem === subcategory.name
                                          ? 'bg-indigo-700 text-white font-semibold shadow-inner'
                                          : 'text-indigo-300 hover:bg-indigo-800 hover:text-white'
                                      }`}
                                      whileHover={{ scale: 1.02, x: 5 }}
                                      whileTap={{ scale: 0.98 }}
                                    >
                                      <span className="truncate">{subcategory.name}</span>
                                    </motion.button>
                                  </motion.li>
                                ))}
                              </motion.ul>
                            )}
                          </AnimatePresence>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

